import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { fetchOperationsAvailableTrucks } from "../../../api/operationsAvailableTrucksService";
import { OperationsAvailableTruckDto } from "../../../interfaces/Operations/operationsAvailableTrucks";
type CopyLocationFilter = "all" | "c1" | "fox";

const toSentenceCase = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const normalizeTrailerType = (trailerType?: string | null) => {
  if (!trailerType?.trim()) {
    return "";
  }

  const trimmed = trailerType.trim();
  const lowerCaseTrailer = trimmed.toLowerCase();

  if (lowerCaseTrailer === "stepdeckregularprofile") {
    return "Step Deck";
  }

  if (lowerCaseTrailer === "stepdecklowprofile") {
    return "Step Deck Low Pro";
  }

  return toSentenceCase(trimmed);
};

const formatTrailerTypeLabel = (trailerType?: string | null) =>
  normalizeTrailerType(trailerType) || "Unknown";

const buildCopyPayload = (
  dateLabel: string,
  trucks: OperationsAvailableTruckDto[],
) => {
  const headers = ["#", "Unit", "Trailer", "Company", "Dispatcher", "Location"];

  const rows = trucks.map((truck, index) => {
    const dispatcher =
      truck.dispatcherNickname?.trim() ||
      truck.dispatcherFullName?.trim() ||
      "Unknown dispatcher";
    const dispatcherWorkPhone = truck.dispatcherWorkPhoneNumber?.trim();
    const dispatcherCell = dispatcherWorkPhone
      ? `${dispatcher} (${dispatcherWorkPhone})`
      : dispatcher;

    return [
      `${index + 1}`,
      truck.truckUnitNumber?.trim() || "N/A",
      normalizeTrailerType(truck.trailerType),
      truck.driverCompany?.trim() || "Unknown company",
      dispatcherCell,
      truck.pickupLocation?.trim() || "Unknown location",
    ];
  });

  const headerHtml = headers
    .map(
      (header) =>
        `<th style="border:1px solid #ccc;padding:4px 8px;text-align:left;white-space:nowrap;">${header}</th>`,
    )
    .join("");

  const bodyHtml = rows
    .map(
      (row) =>
        `<tr>${row
          .map(
            (cell) =>
              `<td style="border:1px solid #ccc;padding:4px 8px;text-align:left;white-space:nowrap;">${cell}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");

  const htmlTable = `
<table style="border-collapse:collapse;font-family:Arial,Helvetica,sans-serif;font-size:12px;">
  <thead>
    <tr>${headerHtml}</tr>
  </thead>
  <tbody>
    ${bodyHtml}
  </tbody>
</table>`;

  // Fallback plain-text (tab-separated) version for apps that ignore HTML.
  const tsvLines = [headers.join("\t"), ...rows.map((row) => row.join("\t"))];
  const plainText = `Available trucks for ${dateLabel}\n\n${tsvLines.join(
    "\n",
  )}`;

  return {
    html: `<div><strong>Available trucks for ${dateLabel}</strong><br/>${htmlTable}</div>`,
    text: plainText,
  };
};

const OperationsAvailableTrucks = () => {
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf("day"));
  const [copyCompanyFilter, setCopyCompanyFilter] =
    useState<CopyLocationFilter>("all");
  const [trailerTypeFilter, setTrailerTypeFilter] = useState<string>("all");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const selectedDateKey = useMemo(
    () => selectedDate.format("YYYY-MM-DD"),
    [selectedDate],
  );

  const selectedDateLabel = useMemo(
    () => selectedDate.format("dddd, MMMM D, YYYY"),
    [selectedDate],
  );

  const {
    data: trucks = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<OperationsAvailableTruckDto[], Error>({
    queryKey: ["operations-available-trucks", selectedDateKey],
    queryFn: () => fetchOperationsAvailableTrucks(token, selectedDateKey),
    enabled: Boolean(token),
    placeholderData: (previousData) => previousData,
  });

  const trailerTypeOptions = useMemo(() => {
    const options = trucks
      .map((truck) => formatTrailerTypeLabel(truck.trailerType))
      .filter((type) => type.trim());

    return Array.from(new Set(options)).sort((a, b) => a.localeCompare(b));
  }, [trucks]);

  const filteredTrucks = useMemo(() => {
    if (trailerTypeFilter === "all") {
      return trucks;
    }

    const normalizedFilter = trailerTypeFilter.toLowerCase();

    return trucks.filter(
      (truck) =>
        formatTrailerTypeLabel(truck.trailerType).toLowerCase() ===
        normalizedFilter,
    );
  }, [trailerTypeFilter, trucks]);

  const trucksWithLocation = useMemo(
    () => filteredTrucks.filter((truck) => truck.pickupLocation?.trim()),
    [filteredTrucks],
  );

  const filteredCopyTrucks = useMemo(() => {
    if (copyCompanyFilter === "all") {
      return trucksWithLocation;
    }

    return trucksWithLocation.filter((truck) => {
      const driverCompany = truck.driverCompany?.toLowerCase() || "";

      if (copyCompanyFilter === "c1") {
        return driverCompany.includes("c1");
      }

      if (copyCompanyFilter === "fox") {
        return driverCompany.includes("fox");
      }

      return true;
    });
  }, [copyCompanyFilter, trucksWithLocation]);

  const handleDateChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }

    setSelectedDate(value.startOf("day"));
  };

  const handleCopy = async () => {
    if (!filteredCopyTrucks.length) {
      setSnackbar({
        open: true,
        message: "There are no trucks to copy with the selected filters.",
        severity: "error",
      });
      return;
    }

    try {
      const payload = buildCopyPayload(selectedDateLabel, filteredCopyTrucks);
      if (navigator.clipboard && "write" in navigator.clipboard) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([payload.html], { type: "text/html" }),
            "text/plain": new Blob([payload.text], { type: "text/plain" }),
          }),
        ]);
      } else if (navigator.clipboard && "writeText" in navigator.clipboard) {
        await (navigator.clipboard as Clipboard).writeText(payload.text);
      }
      setSnackbar({
        open: true,
        message: "Truck list copied to your clipboard.",
        severity: "success",
      });
    } catch (copyError) {
      setSnackbar({
        open: true,
        message: "Unable to copy the truck list. Please try again.",
        severity: "error",
      });
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" mt={6}>
          <CircularProgress />
        </Box>
      );
    }

    if (isError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error?.message || "Something went wrong while loading trucks."}
        </Alert>
      );
    }

    if (!filteredTrucks.length) {
      return (
        <Alert severity="info">
          No available trucks found for {selectedDateLabel}.
        </Alert>
      );
    }

    return (
      <TableContainer component={Card} elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="10%">#</TableCell>
              <TableCell width="15%">Unit</TableCell>
              <TableCell width="15%">Driver</TableCell>
              <TableCell width="15%">Trailer</TableCell>
              <TableCell width="15%">Company</TableCell>
              <TableCell width="15%">Dispatcher</TableCell>
              <TableCell width="15%">Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTrucks.map((truck, index) => {
              const dispatcher = truck.dispatcherNickname || "N/A";
              const driverName = truck.driverName?.trim() || "Unknown driver";

              return (
                <TableRow key={`${truck.truckUnitNumber}-${index}`} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{truck.truckUnitNumber || "N/A"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {driverName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {formatTrailerTypeLabel(truck.trailerType)}
                  </TableCell>
                  <TableCell>
                    {truck.driverCompany ? (
                      <Chip
                        size="small"
                        color="secondary"
                        variant="outlined"
                        label={truck.driverCompany}
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      "Unknown"
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        {dispatcher}
                      </Typography>
                      {truck.dispatcherWorkPhoneNumber && (
                        <Typography variant="body2" color="text.secondary">
                          {truck.dispatcherWorkPhoneNumber}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{truck.pickupLocation}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box p={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        mb={3}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <LocalShippingRoundedIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Available trucks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View trucks without loads for the selected day and share the list.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date"
              value={selectedDate}
              onChange={handleDateChange}
              format="MMMM D, YYYY"
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
            />
          </LocalizationProvider>
          <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 180 } }}>
            <InputLabel id="trailer-type-filter-label">Trailer type</InputLabel>
            <Select
              labelId="trailer-type-filter-label"
              label="Trailer type"
              value={trailerTypeFilter}
              onChange={(event) =>
                setTrailerTypeFilter(event.target.value as string)
              }
            >
              <MenuItem value="all">All trailers</MenuItem>
              {trailerTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh list">
            <span>
              <IconButton
                onClick={() => refetch()}
                disabled={isFetching}
                color="primary"
              >
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <ToggleButtonGroup
            color="primary"
            size="small"
            value={copyCompanyFilter}
            exclusive
            onChange={(_event, value) => value && setCopyCompanyFilter(value)}
            aria-label="Copy company filter"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="c1">C1</ToggleButton>
            <ToggleButton value="fox">Fox</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Copy trucks to clipboard">
            <span>
              <Button
                variant="contained"
                startIcon={<ContentCopyRoundedIcon />}
                onClick={handleCopy}
                disabled={!filteredCopyTrucks.length}
              >
                Copy list
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {renderContent()}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OperationsAvailableTrucks;
